// Reusable month/week calendar for Nordic Yoga sessions.
// Props:
//   sessions: array of session objects (must have id, title, start_at, discipline, cancelled?, price_eur, location, booked_count?, max_participants, places_left?)
//   onSelect: (session) => void — called when user clicks on a pill.
//   initialView: "month" | "week" (default "month")
//   testidPrefix: for data-testid namespacing
import { useMemo, useState } from "react";
import {
  addDays, addMonths, addWeeks, endOfMonth, endOfWeek, format,
  isSameDay, isSameMonth, isToday, startOfDay, startOfMonth, startOfWeek, subMonths, subWeeks,
} from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DISCIPLINE_STYLE = {
  nordic_yoga: { bg: "#E8F0EC", fg: "#2E5A44", dot: "#2E5A44" },
  marche_nordique: { bg: "#F5EBDC", fg: "#8B5A20", dot: "#8B5A20" },
  stage: { bg: "#E7E9F0", fg: "#1B3A4B", dot: "#1B3A4B" },
};

function styleFor(discipline) {
  return DISCIPLINE_STYLE[discipline] || { bg: "#F1F5F9", fg: "#334155", dot: "#334155" };
}

function Pill({ session, onSelect, testidPrefix }) {
  const s = styleFor(session.discipline);
  const cancelled = !!session.cancelled;
  const discovery = !!session.is_discovery;
  const hh = format(new Date(session.start_at), "HH:mm");
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onSelect?.(session); }}
      className={`w-full text-left text-[11px] leading-tight px-1.5 py-1 rounded-md truncate inline-flex items-center gap-1 hover:ring-1 hover:ring-[var(--forest)] transition ${cancelled ? "line-through opacity-60" : ""} ${discovery ? "ring-1 ring-[var(--terracotta)]" : ""}`}
      style={{ background: s.bg, color: s.fg }}
      title={`${hh} · ${session.title} · ${session.location}${discovery ? " · Séance découverte" : ""}`}
      data-testid={`${testidPrefix}-pill-${session.id}`}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: s.dot }} />
      {discovery && <span className="text-[10px]">🌱</span>}
      <span className="font-semibold">{hh}</span>
      <span className="truncate">{session.title}</span>
    </button>
  );
}

export default function SessionsCalendar({ sessions = [], onSelect, initialView = "month", testidPrefix = "calendar" }) {
  const [view, setView] = useState(initialView);
  const [cursor, setCursor] = useState(() => startOfDay(new Date()));

  const byDay = useMemo(() => {
    const map = new Map();
    for (const s of sessions) {
      const d = new Date(s.start_at);
      const key = format(d, "yyyy-MM-dd");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    // sort each day chronologically
    for (const list of map.values()) {
      list.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    }
    return map;
  }, [sessions]);

  const goPrev = () => setCursor(c => view === "month" ? subMonths(c, 1) : subWeeks(c, 1));
  const goNext = () => setCursor(c => view === "month" ? addMonths(c, 1) : addWeeks(c, 1));
  const goToday = () => setCursor(startOfDay(new Date()));

  const title = view === "month"
    ? format(cursor, "MMMM yyyy", { locale: fr })
    : (() => {
        const s = startOfWeek(cursor, { weekStartsOn: 1 });
        const e = endOfWeek(cursor, { weekStartsOn: 1 });
        return `${format(s, "d MMM", { locale: fr })} — ${format(e, "d MMM yyyy", { locale: fr })}`;
      })();

  return (
    <div className="card-warm p-4" data-testid={`${testidPrefix}-root`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={goPrev} className="p-2 rounded-full hover:bg-slate-100 text-[var(--pine)]" aria-label="Précédent" data-testid={`${testidPrefix}-prev`}>
            <ChevronLeft size={18}/>
          </button>
          <button onClick={goToday} className="text-xs px-3 py-1.5 rounded-full border border-[var(--pine)]/20 text-[var(--pine)] hover:bg-[var(--pine)] hover:text-white transition" data-testid={`${testidPrefix}-today`}>
            Aujourd'hui
          </button>
          <button onClick={goNext} className="p-2 rounded-full hover:bg-slate-100 text-[var(--pine)]" aria-label="Suivant" data-testid={`${testidPrefix}-next`}>
            <ChevronRight size={18}/>
          </button>
          <div className="ml-3 font-serif-display text-xl font-semibold capitalize" style={{ color: "var(--pine)" }} data-testid={`${testidPrefix}-title`}>{title}</div>
        </div>
        <div className="inline-flex items-center rounded-full bg-slate-100 p-1 text-xs">
          {[
            { v: "month", label: "Mois" },
            { v: "week", label: "Semaine" },
          ].map(o => (
            <button key={o.v} onClick={() => setView(o.v)}
              className={`px-3 py-1 rounded-full transition ${view === o.v ? "bg-[var(--pine)] text-white" : "text-[var(--muted-text)] hover:text-[var(--pine)]"}`}
              data-testid={`${testidPrefix}-view-${o.v}`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-[11px] text-[var(--muted-text)] mb-3 flex-wrap">
        {[
          { key: "nordic_yoga", label: "Nordic Yoga" },
          { key: "marche_nordique", label: "Marche Nordique" },
          { key: "stage", label: "Stage" },
        ].map(d => {
          const st = styleFor(d.key);
          return (
            <span key={d.key} className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: st.dot }}/>{d.label}
            </span>
          );
        })}
      </div>

      {view === "month" ? (
        <MonthGrid cursor={cursor} byDay={byDay} onSelect={onSelect} testidPrefix={testidPrefix} />
      ) : (
        <WeekGrid cursor={cursor} byDay={byDay} onSelect={onSelect} testidPrefix={testidPrefix} />
      )}
    </div>
  );
}

function MonthGrid({ cursor, byDay, onSelect, testidPrefix }) {
  const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
  const days = [];
  for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
  const weekdays = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

  return (
    <div data-testid={`${testidPrefix}-month-grid`}>
      <div className="grid grid-cols-7 mb-1 text-[11px] uppercase tracking-wide text-[var(--muted-text)]">
        {weekdays.map(w => <div key={w} className="px-2 py-1 text-center">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map(d => {
          const key = format(d, "yyyy-MM-dd");
          const list = byDay.get(key) || [];
          const outside = !isSameMonth(d, cursor);
          const today = isToday(d);
          return (
            <div key={key}
              className={`min-h-[92px] rounded-lg p-1.5 border transition ${outside ? "bg-slate-50 border-slate-100 opacity-70" : "bg-white border-slate-200"} ${today ? "ring-2 ring-[var(--terracotta)]" : ""}`}
              data-testid={`${testidPrefix}-day-${key}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-semibold ${today ? "text-[var(--terracotta)]" : "text-[var(--pine)]"}`}>{format(d, "d")}</span>
                {list.length > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{list.length}</span>
                )}
              </div>
              <div className="space-y-1">
                {list.slice(0, 3).map(s => (
                  <Pill key={s.id} session={s} onSelect={onSelect} testidPrefix={testidPrefix} />
                ))}
                {list.length > 3 && (
                  <div className="text-[10px] text-[var(--muted-text)] pl-1">+{list.length - 3} de plus</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekGrid({ cursor, byDay, onSelect, testidPrefix }) {
  const start = startOfWeek(cursor, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-2 md:grid-cols-7 gap-2" data-testid={`${testidPrefix}-week-grid`}>
      {days.map(d => {
        const key = format(d, "yyyy-MM-dd");
        const list = byDay.get(key) || [];
        const today = isToday(d);
        return (
          <div key={key}
            className={`rounded-lg p-2 border bg-white ${today ? "ring-2 ring-[var(--terracotta)] border-transparent" : "border-slate-200"}`}
            data-testid={`${testidPrefix}-day-${key}`}
          >
            <div className={`text-xs uppercase tracking-wide ${today ? "text-[var(--terracotta)]" : "text-[var(--muted-text)]"}`}>
              {format(d, "EEE", { locale: fr })}
            </div>
            <div className={`font-serif-display text-2xl font-semibold ${today ? "text-[var(--terracotta)]" : "text-[var(--pine)]"}`}>
              {format(d, "d")}
            </div>
            <div className="mt-2 space-y-1 min-h-[40px]">
              {list.length === 0 && (
                <div className="text-[11px] text-slate-300">—</div>
              )}
              {list.map(s => (
                <Pill key={s.id} session={s} onSelect={onSelect} testidPrefix={testidPrefix} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
